import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  Heading,
  Text,
  Button,
  Flex,
  Link,
  HStack,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Image,
  Spinner,
  Alert,
  AlertIcon,
  Tag,
} from "@chakra-ui/react";
import { StarIcon } from "@chakra-ui/icons";
import { fetchUserDevices } from "../services/api";
import { getProductImageFromStructure } from '../utils/getProductImageFromStructure';
import { productStructure } from "../services/productStructure";

const DashboardHome = ({ firstName }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadUserDevices = async () => {
      setLoading(true);
      try {
        const devices = await fetchUserDevices();
        setProducts(devices);
        setError(null);
      } catch (err) {
        console.error("Failed to load devices:", err);
        setError("Failed to load your devices. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadUserDevices();
  }, []);

  return (
    <VStack spacing={6} align="stretch">
      <Heading as="h1" size="xl">
        Welcome to Calibrite, {firstName || "User"}
      </Heading>
      <Text>
        We're glad to see you on our "Account Portal". Your personalized
        dashboard is ready—track your activity, manage your settings, and stay
        in control of your account. If you need any help, we're just a click
        away.
      </Text>

      {/* Special Offer Section */}
      <Box borderWidth="1px" borderRadius="lg" p={4} borderColor="gray.300">
        <HStack justify="space-between" align="start">
          <VStack align="start" spacing={3}>
            <Tag colorScheme="green" size="sm">
              <StarIcon mr={1} />
              Special Offer
            </Tag>
            <Text fontWeight="bold">Save 10% on our most popular products</Text>
            <Text>
              Upgrade yourself or a friend with the most popular products for
              your creative workflow with the discount code:{" "}
              <Text as="span" fontWeight="bold">
                calibrite2025
              </Text>
            </Text>
            <Link href="#" color="blue.500" fontSize="sm">
              Show offer items
            </Link>
          </VStack>
          <Button colorScheme="blue" mt={4}>
            Upgrade now
          </Button>
        </HStack>
      </Box>

      {/* Products and Services Section */}
      <Box borderWidth="1px" borderRadius="lg" p={4} borderColor="gray.300">
        <Heading as="h2" size="md" mb={3}>
          Your Products and Services
        </Heading>
        <Text mb={4}>
          Here's everything you need to know about the products linked to your
          account. This overview gives you a clear picture of your current
          registrations, making it easy to manage what you have. Whether you're
          updating existing products or adding new ones, everything you need is
          right here.
        </Text>

        {error && (
          <Alert status="error" mb={4}>
            <AlertIcon />
            {error}
          </Alert>
        )}

        {loading ? (
          <Flex justify="center" align="center" py={10}>
            <Spinner size="xl" />
          </Flex>
        ) : (
          <TableContainer>
            <Table colorScheme="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Image</Th>
                  <Th>Product Type</Th>
                  <Th>Product Model</Th>
                  <Th>Device Serial</Th>
                  <Th>Purchase Date</Th>
                  <Th>Registration Date</Th>
                </Tr>
              </Thead>
              <Tbody>
                {products.length > 0 ? (
                  products.map((product, index) => (
                    <Tr key={index}>
                      <Td>
                        <Image
                          src={getProductImageFromStructure(
                            productStructure,
                            product.productType,
                            product.productModel
                          )}
                          alt={product.productType}
                          boxSize="40px"
                          objectFit="contain"
                          fallbackSrc="https://via.placeholder.com/40"
                        />
                      </Td>
                      <Td>{product.productType}</Td>
                      <Td>{product.productModel}</Td>
                      <Td>{product.serialNumber}</Td>
                      <Td>{product.purchaseDate || "N/A"}</Td>
                      <Td>{product.registrationDate || "N/A"}</Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td colSpan={6} textAlign="center" py={4}>
                      <Text>No products registered yet.</Text>
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </VStack>
  );
};

export default DashboardHome;
